/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CoreModule
 */

import {
    Component,
    Injectable,
    Type,
} from '@angular/core';

import {
    BehaviorSubject,
    Observable,
    Subject,
} from 'rxjs';

import { findLastIndex } from 'underscore';

export interface IFullModalLayout {
    component: Type<Component>;
    componentProps?: Record<string, any>;
    previewComponent?: Type<Component>;
    previewComponentProps?: Record<string, any>;
    scrollToSelector?: string;
    getDescription?(): string;
    getName?(): string;
}

// Token used when registering as a downgraded service in AJS.
export const FULL_MODAL_SERVICE_TOKEN = 'fullModalService';

/**
 * @ngdoc service
 * @name  FullModalService
 * @description Service for adding and removing full modals.
 * @author alextsg
 */
@Injectable({
    providedIn: 'root',
})
export class FullModalService {
    public modalLayoutStack: IFullModalLayout[] = [];

    /**
     * Returns the modalLayoutStack, a stack of all modal layouts that the user is working with.
     */
    public modalLayoutStack$: Observable<IFullModalLayout[]>;

    /**
     * Returns the viewMode, which indicates if the user is editing or viewing the modal.
     */
    public viewMode$: Observable<boolean>;

    /**
     * Subject for the modalLayoutStack.
     */
    private modalLayoutStackSubject = new Subject<IFullModalLayout[]>();

    /**
     * BehaviorSubject for the viewMode flag.
     */
    private viewMode = new BehaviorSubject<boolean>(false);

    constructor() {
        this.modalLayoutStack$ = this.modalLayoutStackSubject.asObservable();
        this.viewMode$ = this.viewMode.asObservable();
    }

    /**
     * Adds a modal layout to the end of the modalLayoutStack.
     */
    public addModal(modalLayout: IFullModalLayout): void {
        this.modalLayoutStack.push(modalLayout);
        this.modalLayoutStackSubject.next(this.modalLayoutStack);
    }

    /**
     * Checks whether a modal is opened by modal component.
     */
    public isOpen(component: Type<Component>): boolean {
        return this.modalLayoutStack.some(
            (modalLayout: IFullModalLayout) => modalLayout.component === component,
        );
    }

    /**
     * Removes a modal by component class. Looks through the modalLayoutStack for the last component
     * class that matches, and removes it from the stack.
     */
    public removeModalByComponent(component: Type<Component>): void {
        const modalLayoutToRemoveIndex = findLastIndex(
            this.modalLayoutStack,
            (modalLayout: IFullModalLayout) => modalLayout.component === component,
        );

        this.modalLayoutStack.splice(modalLayoutToRemoveIndex, 1);
        this.modalLayoutStackSubject.next(this.modalLayoutStack);
    }

    /**
     * Removes the last modal layout from the modalLayoutStack.
     */
    public removeLastModal(): void {
        this.modalLayoutStack.pop();
        this.modalLayoutStackSubject.next(this.modalLayoutStack);
    }

    /**
     * Removes all modal layouts from the modalLayoutStack.
     */
    public removeAllModals = (): void => {
        this.modalLayoutStack.length = 0;
        this.modalLayoutStackSubject.next(this.modalLayoutStack);
    };

    /**
     * Set viewMode to true for readonly/view mode and false for edit mode.
     */
    public setViewMode(viewMode = false): void {
        this.viewMode.next(viewMode);
    }
}
